import { Inject } from '@odinjs/odin';

import { Endpoint } from '@philips-emr/hpms-stipulator-commons';
import { FieldListAPI, FieldsetAPI, PanelAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { AnexoTissAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import './anexo-tiss-controller.css';

export enum TipoOrigemAnexo {
  ANEXOS_TISS_REQUISICOES = 'anexos-tiss-requisicoes',
  ANEXOS_TISS_AUTORIZACOES = 'anexos-tiss-autorizacoes',
}

enum TypePromisse {
  QUIMIO,
  DIAGNOSTICO,
}

interface PromiseStatus {
  done: boolean;
  hasData: boolean;
  type: TypePromisse;
}

@Controller({ fragment: FragmentNames.ANEXO_TISS })
class AnexoTissController extends BaseController {

  @Inject(Aliases.PANEL_QUIMIO)
  private panelQuimio: PanelAPI;
  @Inject(Aliases.PANEL_QUIMIOTERAPIA_DADO)
  private panelQuimioDado: PanelAPI;
  @Inject(Aliases.PANEL_DIAGNOSTICO_QUIMIO)
  private panelDiagnosticoQuimio: PanelAPI;
  @Inject(Aliases.PANEL_OPME)
  private panelOpme: PanelAPI;
  @Inject(Aliases.PANEL_RADIO)
  private panelRadio: PanelAPI;
  @Inject(Aliases.PANEL_DIAGNOSTICO_RADIO)
  public panelDiagnosticoRadio: PanelAPI;
  @Inject(Aliases.FIELDSET_CID_QUIMIO)
  private fieldsetCidQuimio: FieldsetAPI;
  @Inject(Aliases.FIELDSET_CID_RADIO)
  private fieldsetCidRadio: FieldsetAPI;
  @Inject(Aliases.PANEL_SITUACAO_INICIAL)
  private panelSituacaoInicial: PanelAPI;
  private fetchQuimio: PromiseStatus;
  private fetchDiagnostico: PromiseStatus;

  private initPromiseStatus(type: TypePromisse): PromiseStatus {
    return { type, done: false, hasData: false };
  }

  @OnMount(Aliases.LAYOUT_ANEXO_TISS)
  public onMountLayoutAutorizacaoDadosGuia(): void {
    this.fetchQuimio = this.initPromiseStatus(TypePromisse.QUIMIO);
    this.fetchDiagnostico = this.initPromiseStatus(TypePromisse.DIAGNOSTICO);

    const origem = this.context.get('origem') as TipoOrigemAnexo;
    this.context.set({ origem });
  }

  @OnMount(Aliases.HPMS_CUSTOM_LIST_FIELD_SITUACAO_CLINICA)
  onMountListFieldSituacaoClinica(event: MountEvent): void {
    const api = event.target as FieldListAPI;
    const { nrSeqContrato, origem, nrSequencia } = this.context.getBulkData();

    void HttpService.get<Record<string, Value>[]>(Endpoint.GET.SITUACAO_CLINICA, { nrSeqContrato, origem, nrSequencia })
      .then(({ body }) => {
        if (body) {
          api.setValue(body);
        }
      });
  }

  @OnAfterFetch(Aliases.FETCH_QUIMIO_DADO)
  public onAfterFetchQuDado(event: FetchEvent): void {
    this.fetchQuimio.done = true;

    if (!event.data) {
      this.panelQuimioDado.setVisible(false);
      this.setVisibilidadePanelQuimio(this.fetchDiagnostico.done, this.fetchDiagnostico.hasData);
    } else {
      this.fetchQuimio.hasData = true;
    }
  }

  @OnAfterFetch(Aliases.FETCH_QUIMIO_DIAGNOSTICO)
  public onAfterFetchQuDiag(event: FetchEvent): void {
    this.fetchDiagnostico.done = true;

    if (!event.data) {
      this.panelDiagnosticoQuimio.setVisible(false);
      this.setVisibilidadePanelQuimio(this.fetchQuimio.done, this.fetchQuimio.hasData);
    } else {
      this.fetchDiagnostico.hasData = true;
    }
  }

  @OnAfterFetch(Aliases.FETCH_QUIMIO_CID)
  public onAfterFetchQuCid(event: FetchEvent): void {
    if (!event.data) {
      this.fieldsetCidQuimio.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_OPME_DADO)
  public onAfterFetchOpDado(event: FetchEvent): void {
    if (!event.data) {
      this.panelOpme.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_RADIO_DADO)
  public onAfterFetchRaDado(event: FetchEvent): void {
    if (!event.data) {
      this.panelRadio.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_RADIO_DIAGNOSTICO)
  public onAfterFetchRaDiag(event: FetchEvent): void {
    if (!event.data) {
      this.panelDiagnosticoRadio.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_RADIO_CID)
  public onAfterFetchRaCid(event: FetchEvent): void {
    if (!event.data) {
      this.fieldsetCidRadio.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_SITUACAO_INICIAL_DADO)
  public onAfterFetchSitacaoInicialDado(event: FetchEvent): void {
    if (!event.data) {
      this.panelSituacaoInicial.setVisible(false);
    }
  }

  private setVisibilidadePanelQuimio(done: boolean, hasData: boolean): void {
    if (done && !hasData && this.panelQuimio.isVisible()) {
      this.panelQuimio.setVisible(false);
    }
  }
}

export default {
  AnexoTissController,
};
